/**
 * Block 13 PR-G Phase 4A — Section «Стоимость и расчёты» + срок изготовления.
 *
 * Fields:
 *   - costSource chip-segment («Из КП» / «Вручную»; default 'manual' per PRD)
 *   - totalCost (read-only display, value = selectTotalSpec(state) per Phase
 *     6.5G hotfix round 3 R3-#1 — user меняет сумму через позиции Спецификации)
 *   - advance (numeric input)
 *   - remaining (readonly derived = selectTotalSpec(state) - advance, clamped к 0)
 *   - durationDays (numeric input — Срок изготовления в рабочих днях)
 *
 * **Advance auto-suggest:** Phase 4A — НЕ auto-set advance = totalCost/2 при
 * первом вводе totalCost (как в прототипе) — это вызывает confusing state
 * transitions. User вводит advance отдельно. Если потребуется restore — добавим
 * в Phase 5 / 4B re-review.
 *
 * **Cost source 'fromQuote':** Phase 4A — chip just toggles costSource state.
 * Phase 4C wires actual quote selection sheet (ChoiceSheetForQuotes) для
 * подтягивания cost from saved-quote IDB. До Phase 4C — пользователь видит
 * chip selected но cost вводит вручную (graceful — manual input is always
 * available regardless of source state).
 *
 * Reference: prototype `screen-2.13.2-contract.html` lines 424-457.
 */

import { useCallback, useRef, type Dispatch } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  CONTRACT_CURRENCY_CODES,
  getCurrencyShort,
  getCurrencyWord,
  isAdvancePercentMode,
  type ContractCurrencyCode,
} from '@pomoshnik/shared';

import { genUuid } from '@/lib/uuid';
import { SectionCard } from '@/features/calculator/anketa/components/SectionCard';
import { SectionHeader } from '@/features/calculator/anketa/components/SectionHeader';

import { MissingFieldsBadge } from '../components/MissingFieldsBadge';
import {
  selectAdvance,
  selectCostSectionMissing,
  selectRemaining,
  selectTotalSpec,
} from '../state/contractSelectors';
import type { ContractAction, ContractFormState } from '../state/contractReducer';

const INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

const NUMERIC_INPUT_CLASS = `${INPUT_CLASS} pr-12`;
// Phase 6.5D Item #2 — native <select> для currency. Mirror INPUT_CLASS visual
// styling + reserve right padding под browser dropdown chevron arrow.
const SELECT_CLASS = `${INPUT_CLASS} pr-10 appearance-auto`;
const READONLY_INPUT_CLASS =
  'w-full min-h-tap px-3 pr-12 rounded-xl border-[1.5px] border-accent bg-canvas text-base ' +
  'text-ink/70 cursor-not-allowed';

const LABEL_CLASS = 'block text-sm font-semibold text-ink/80 mb-1';
const CUSTOM_LABEL_INPUT_CLASS = `${INPUT_CLASS} text-sm font-semibold text-ink/80 mb-1.5`;

// Phase 6.5C Aibek decision Q1 — chip-segment options keyed by ContractCurrencyCode.
// Labels = nominative form per getCurrencyWord. Default value derives from
// state.currencyCode (seeded по CONTRACT_CURRENCY_PER_REGION в createInitialContractDraft);
// override allowed (KZ furniture maker → договор в RUB для RU клиента).
const CURRENCY_OPTIONS = CONTRACT_CURRENCY_CODES.map((code) => ({
  value: code,
  label: getCurrencyWord(code),
}));

// Phase 6.5C ADR Amendment A3 — UX cap 20 custom rows. Beyond — user обычно
// должен use SectionSpec вместо cost section.
const COST_CUSTOM_ROWS_CAP = 20;

export interface SectionCostAndTermsProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
}

export function SectionCostAndTerms({ state, dispatch }: SectionCostAndTermsProps) {
  // Phase 6.5B Amendment A1 (2026-05-24) — currency источник перешёл из
  // template.meta.* → user-controlled draft.currencyCode + shared helpers.
  // UI chip-segment «тенге / рубль / сум» + cleaned labels ships в 6.5C.
  const currencyShort = getCurrencyShort(state.currencyCode);
  const currencyWord = getCurrencyWord(state.currencyCode);
  // Phase 6.5G hotfix round 3 (R3-#1) — Сумма auto-propagation from Spec.
  // selectTotalSpec — единственный источник Суммы для UI/PDF/selectors. User
  // меняет сумму через позиции Спецификации, не вручную здесь.
  const totalCost = selectTotalSpec(state);
  const remaining = selectRemaining(state);
  // PR-D2 (ADR-0040) — ставка «Аванс, %» + эффективная сумма аванса. При
  // заданной ставке поле суммы read-only (Вариант A), сумма = производное.
  const percentMode = isAdvancePercentMode(state.advancePercent);
  const effectiveAdvance = selectAdvance(state);

  // Phase 6.5 #14 — section badge при суммарной стоимости === 0. Подсказывает
  // где заполнять для разблокировки PDF / Share.
  const showMissing = selectCostSectionMissing(state);

  function handleNumberInput(path: 'advance' | 'durationDays') {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Empty string → 0 (user clearing field). Negative inputs clamped к 0
      // защитно (Zod schema validates min(0) при save).
      const parsed = raw === '' ? 0 : Math.max(0, Number(raw) || 0);
      dispatch({ type: 'SET_FIELD', update: { path, value: parsed } });
    };
  }

  // PR-D2 (ADR-0040) — ставка «Аванс, %» (0–100). '' → null (ручной режим),
  // иначе число clamp'нутое к [0, 100] (Zod validate min/max при save).
  function handleAdvancePercentInput(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const value = raw === '' ? null : Math.min(100, Math.max(0, Number(raw) || 0));
    dispatch({ type: 'SET_FIELD', update: { path: 'advancePercent', value } });
  }

  // Phase 7 hotfix round 6 (R6-#1, 2026-05-26) — ref-debounce 300ms guard для
  // «+ Добавить строку» в costCustomRows. R5-#3 touch-manipulation alone не
  // помог (Aibek visual verify round 11). Phase A code-level investigation не
  // дал conclusive root cause без iOS device; Phase B fallback гарантирует
  // single dispatch независимо от первоисточника double-fire. См. handover R6.
  const lastAddCostRowClickRef = useRef<number>(0);
  const handleAddCostRowDebounced = useCallback(() => {
    const now = Date.now();
    if (now - lastAddCostRowClickRef.current < 300) {
      if (import.meta.env.DEV) {
        console.warn('[SectionCostAndTerms] add cost row click debounced (gap < 300ms)');
      }
      return;
    }
    lastAddCostRowClickRef.current = now;
    dispatch({ type: 'ADD_COST_CUSTOM_ROW', rowId: genUuid() });
  }, [dispatch]);

  return (
    <SectionCard testId="contract-section-cost">
      <div className="flex items-center gap-2 mb-3.5">
        <SectionHeader className="mb-0">Стоимость и расчёты</SectionHeader>
        {showMissing ? <MissingFieldsBadge testId="contract-section-cost-missing-badge" /> : null}
      </div>

      <div className="flex flex-col gap-3">
        {/* Phase 6.5D Item #2 (Aibek decision Q1 = Вариант Б, 2026-05-24) —
            Currency native <select> «Выбери валюту». Default derives from
            region при registration (KZT/RUB/UZS), user override allowed для
            cross-border contracts. Decoupled от template language. ИТОГО в
            SectionSpec retains currency word per ADR A1. */}
        <div>
          <label htmlFor="contract-currency-select" className={LABEL_CLASS}>
            Выбери валюту
          </label>
          <select
            id="contract-currency-select"
            value={state.currencyCode}
            onChange={(e) =>
              dispatch({
                type: 'SET_CURRENCY_CODE',
                value: e.target.value as ContractCurrencyCode,
              })
            }
            // a11y: <label htmlFor="contract-currency-select"> provides accessible
            // name «Выбери валюту» — no explicit aria-label needed (would override
            // visible label, confusing screen reader users).
            data-testid="contract-currency-select"
            className={SELECT_CLASS}
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Phase 6.5G hotfix (2026-05-25) fix #7 — chip-segment «Ввести данные:
            Из КП / Вручную» УДАЛЁН. Выбор источника данных (КП vs вручную)
            теперь делается на entry screen (ChoiceSheetCreateMode введён в
            Phase 6.5G A8) — этот toggle дублировал решение. `state.costSource`
            field preserved в schema (не trogается; downstream wiring intact,
            см. ADR-0025 A8). */}

        {/* Сумма. Phase 6.5G hotfix round 3 (R3-#1) — read-only display из
            selectTotalSpec(state). User меняет сумму через позиции Спецификации;
            inline edit removed (Aibek locked decision 2026-05-25). */}
        <div>
          <label htmlFor="contract-total-cost" className={LABEL_CLASS}>
            Сумма
          </label>
          <div className="relative">
            <input
              id="contract-total-cost"
              type="number"
              readOnly
              aria-readonly="true"
              value={totalCost === 0 ? '' : totalCost}
              aria-label={`Сумма в ${currencyWord}, рассчитывается автоматически из Спецификации`}
              data-testid="contract-total-cost-input"
              className={`${READONLY_INPUT_CLASS} font-bold`}
            />
            <span
              aria-hidden="true"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 text-sm font-semibold pointer-events-none"
            >
              {currencyShort}
            </span>
          </div>
          <p className="text-xs text-ink/60 mt-1">Сумма автоматически из Спецификации</p>
        </div>

        {/* PR-D2 (ADR-0040) — «Аванс, %»: хранимая ставка ПЕРЕД «Аванс».
            Задана → «Аванс» read-only = производная сумма (Вариант A). */}
        <div>
          <label htmlFor="contract-advance-percent" className={LABEL_CLASS}>
            Аванс, %
          </label>
          <input
            id="contract-advance-percent"
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            value={state.advancePercent ?? ''}
            onChange={handleAdvancePercentInput}
            aria-label="Аванс в процентах от суммы"
            data-testid="contract-advance-percent-input"
            className={INPUT_CLASS}
            placeholder="напр. 60"
          />
        </div>

        {/* Аванс + Остаток (Phase 6.5C Q1 — labels cleaned). */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label htmlFor="contract-advance" className={LABEL_CLASS}>
              Аванс
            </label>
            <div className="relative">
              <input
                id="contract-advance"
                type="number"
                inputMode="numeric"
                min={0}
                step={1000}
                readOnly={percentMode}
                aria-readonly={percentMode || undefined}
                value={percentMode ? effectiveAdvance : state.advance === 0 ? '' : state.advance}
                onChange={handleNumberInput('advance')}
                aria-label={
                  percentMode
                    ? `Аванс в ${currencyShort} (рассчитывается из процента)`
                    : `Аванс в ${currencyShort}`
                }
                data-testid="contract-advance-input"
                className={percentMode ? READONLY_INPUT_CLASS : NUMERIC_INPUT_CLASS}
                placeholder="0"
              />
              <span
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 text-sm font-semibold pointer-events-none"
              >
                {currencyShort}
              </span>
            </div>
          </div>
          <div className="flex-1">
            <label htmlFor="contract-remaining" className={LABEL_CLASS}>
              Остаток
            </label>
            <div className="relative">
              <input
                id="contract-remaining"
                type="number"
                readOnly
                aria-readonly="true"
                value={remaining}
                aria-label={`Остаток к доплате в ${currencyShort}, рассчитывается автоматически`}
                data-testid="contract-remaining-display"
                className={READONLY_INPUT_CLASS}
              />
              <span
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 text-sm font-semibold pointer-events-none"
              >
                {currencyShort}
              </span>
            </div>
          </div>
        </div>

        {/* Срок изготовления (Phase 6.5 #6 — убрали «(рабочих дней)» из label
            per Aibek decision; значение всё ещё интерпретируется как дни в
            ContractDraft.durationDays и в PDF render). */}
        <div>
          <label htmlFor="contract-duration" className={LABEL_CLASS}>
            Срок изготовления
          </label>
          <input
            id="contract-duration"
            type="number"
            inputMode="numeric"
            min={1}
            value={state.durationDays === 0 ? '' : state.durationDays}
            onChange={handleNumberInput('durationDays')}
            // a11y: visual label сокращён per Aibek (#6), но screen-reader
            // value без единицы измерения сбивает — keep «в рабочих днях»
            // в aria-label (WCAG разрешает richer aria-label поверх visible).
            aria-label="Срок изготовления в рабочих днях"
            data-testid="contract-duration-input"
            className={INPUT_CLASS}
            placeholder="30"
          />
        </div>

        {/* Phase 6.5C ADR Amendment A3 + Aibek decision #7 — costCustomRows UI.
            «+ Добавить строку» для свободных строк (Срок гарантии нестандартный,
            особые условия по доставке). Empty rows skipped в PDF render.
            Cap 20 — после лимита add button hidden (gentle UX). */}
        <div
          className="flex flex-col gap-2.5"
          data-testid="contract-cost-custom-rows"
          aria-label="Дополнительные строки стоимости"
        >
          {state.costCustomRows.map((row, idx) => {
            const rowName = row.label.trim() || 'без названия';
            return (
              <div key={row.id} className="flex items-end gap-1.5">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) =>
                      dispatch({
                        type: 'UPDATE_COST_CUSTOM_ROW',
                        rowId: row.id,
                        update: { field: 'label', value: e.target.value },
                      })
                    }
                    placeholder="Например: Срок гарантии (нестандартный)"
                    aria-label={`Название дополнительной строки №${idx + 1}`}
                    data-testid={`contract-cost-custom-${row.id}-label-input`}
                    className={CUSTOM_LABEL_INPUT_CLASS}
                  />
                  <input
                    type="text"
                    value={row.value}
                    onChange={(e) =>
                      dispatch({
                        type: 'UPDATE_COST_CUSTOM_ROW',
                        rowId: row.id,
                        update: { field: 'value', value: e.target.value },
                      })
                    }
                    placeholder="—"
                    aria-label={`Значение строки: ${rowName}`}
                    data-testid={`contract-cost-custom-${row.id}-value-input`}
                    className={INPUT_CLASS}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'REMOVE_COST_CUSTOM_ROW', rowId: row.id })}
                  aria-label={`Удалить строку: ${rowName}`}
                  data-testid={`contract-cost-custom-${row.id}-remove`}
                  className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md text-destructive focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
                >
                  <Trash2 size={18} aria-hidden="true" />
                </button>
              </div>
            );
          })}
          {state.costCustomRows.length < COST_CUSTOM_ROWS_CAP ? (
            /* Phase 7 hotfix round 6 (R6-#1, 2026-05-26) — ref-debounce 300ms
               guard (см. handleAddCostRowDebounced выше). R5-#3 touch-manipulation
               сохранён как дополнительная защита, но root fix теперь client-side. */
            <button
              type="button"
              onClick={handleAddCostRowDebounced}
              aria-label="Добавить строку в стоимость"
              data-testid="contract-cost-add-custom-row"
              className="w-full min-h-tap-chip mt-1 rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold flex items-center justify-center gap-2 touch-manipulation focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
            >
              <Plus size={18} aria-hidden="true" />
              Добавить строку
            </button>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}
